<template>
  <div class="print-page">
    <div class="no-print control-bar p-2 mb-2 d-flex justify-content-between align-items-center bg-white shadow-sm border-bottom">
      <h4 class="mb-0 text-primary font-weight-bolder">Staff Requests Report</h4>
      <div>
        <b-button variant="primary" @click="print" size="sm" class="mr-1 shadow-sm">
          <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
          Print Document
        </b-button>
        <b-button variant="outline-secondary" @click="close" size="sm" class="shadow-sm">
          <feather-icon icon="XIcon" size="16" class="mr-50" />
          Close
        </b-button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5 mt-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <h5 class="mt-3 text-secondary">Generating report, please wait...</h5>
    </div>

    <div v-else class="report-content p-4 bg-white">
      <!-- Report Header -->
      <div class="report-header text-center mb-4">
        <h2 class="report-title">STAFF REQUESTS REPORT</h2>
        <div class="report-meta d-flex justify-content-between align-items-end mt-3 border-bottom pb-2">
          <div class="text-left text-muted">
            <p class="mb-0"><strong>Status Filter:</strong> <span class="text-uppercase border px-1 rounded">{{ status }}</span></p>
            <p class="mb-0"><strong>Total Records:</strong> {{ items.length }}</p>
          </div>
          <div class="text-right text-muted">
            <p class="mb-0"><strong>Generated On:</strong></p>
            <p class="mb-0">{{ currentDateTime }}</p>
          </div>
        </div>
      </div>

      <!-- Report Table -->
      <div class="table-responsive">
        <table class="table print-table table-bordered table-striped">
          <thead class="thead-light">
            <tr>
              <th class="text-center" style="width: 5%">S.No</th>
              <th style="width: 25%">Staff Member</th>
              <th style="width: 20%">Campus</th>
              <th style="width: 15%">Designation</th>
              <th style="width: 15%">Contact</th>
              <th style="width: 10%">Type</th>
              <th style="width: 10%">Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in items" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>
                <div class="staff-name font-weight-bold">{{ item.st.name }}</div>
                <div class="staff-father text-muted" v-if="item.st.father_name">
                  <small>F/N: {{ item.st.father_name }}</small>
                </div>
                <div class="staff-id text-muted">
                  <small>ID: {{ item.st.id }}</small>
                </div>
              </td>
              <td class="align-middle">
                <span v-if="item.us && item.us.metaCampusName">{{ item.us.metaCampusName }}</span>
                <span v-else-if="item.st && item.st.school">{{ item.st.school.name }}.{{ item.st.school.branch }}</span>
                <span v-else class="text-muted">N/A</span>
              </td>
              <td class="align-middle">{{ item.st.designation || '-' }}</td>
              <td class="align-middle">{{ item.st.contact_number || '-' }}</td>
              <td class="align-middle text-capitalize">
                <span class="font-weight-bold">{{ item.us.requestCategory }}</span>
              </td>
              <td class="align-middle">
                {{ item.us.requestedOn ? new Date(item.us.requestedOn).toLocaleDateString("en-UK", { year: "numeric", day: "2-digit", month: "short" }) : '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-if="items.length === 0" class="empty-state text-center p-5 mt-4 border border-dashed rounded">
        <feather-icon icon="InboxIcon" size="48" class="text-muted mb-2" />
        <h5 class="text-secondary">No records found for the selected view.</h5>
        <p class="text-muted mb-0">Try changing your filters or running the report again later.</p>
      </div>

      <!-- Report Footer (Print Only) -->
      <div class="report-footer mt-5 pt-3 border-top text-center text-muted no-screen">
        <small>*** End of Report ***</small>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  name: "StaffRequestsPrint",
  components: {
    BButton,
    BSpinner,
  },
  data() {
    return {
      items: [],
      loading: false,
      status: this.$route.query.status || "pending",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-UK", {
        year: "numeric",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      });
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    ...mapActions({ get: "get" }),
    async loadData() {
      this.loading = true;
      try {
        const { db, cID, status } = this.$route.query;
        // We pass pageSize=2000 to grab "all" bulk data in one go from paginated API endpoint
        const response = await this.get({
          url: `${this.$store.state.domain}StaffRequests/LoadData?db=${db}&cID=${cID}&status=${status}&page=1&pageSize=2000`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        // Safely map API response shapes based on backend nesting
        let fetchedItems = [];
        if (response && response.data && response.data.data) {
          fetchedItems = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (response && response.data && Array.isArray(response.data)) {
          fetchedItems = response.data;
        } else if (response && Array.isArray(response)) {
          fetchedItems = response;
        }
        
        this.items = fetchedItems;
      } catch (error) {
        console.error("Error loading print data:", error);
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style scoped>
.print-page {
  background: #f1f5f9;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding-bottom: 2rem;
}

.control-bar {
  position: sticky;
  top: 0;
  z-index: 1000;
}

.report-content {
  max-width: 1050px;
  margin: 1.5rem auto 0;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
}

.report-title {
  font-weight: 800;
  letter-spacing: 1px;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.report-meta p {
  font-size: 0.85rem;
  color: #64748b;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.print-table th {
  background-color: #f8fafc !important;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.75rem;
  border-bottom: 2px solid #e2e8f0;
}

.print-table td {
  padding: 0.75rem;
  font-size: 0.85rem;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.print-table tbody tr:hover {
  background-color: #f8fafc;
}

.empty-state {
  background: #f8fafc;
}

.staff-name {
  color: #0f172a;
  font-size: 0.9rem;
}

.staff-father, .staff-id {
  font-size: 0.75rem;
}

@media screen {
  .no-screen {
    display: none !important;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    background: white;
    min-height: auto;
    padding: 0;
  }
  .report-content {
    box-shadow: none;
    max-width: 100%;
    margin: 0;
    padding: 0 !important;
    border-radius: 0;
  }
  body {
    background: white;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .print-table th {
    background-color: #f8fafc !important;
    color: #000;
  }
  .report-meta {
    border-bottom: 2px solid #000 !important;
  }
}
</style>
